import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Mail, MessageSquare, Zap, Clock } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import { useWindowWidth } from '../hooks/useWindowWidth';

const perks = [
  { icon: <Zap size={20} />, title: 'Fast Turnaround', desc: 'We respond to all inquiries within 24 hours.' },
  { icon: <MessageSquare size={20} />, title: 'Free Consultation', desc: 'Get a no-obligation consultation on your project.' },
  { icon: <Clock size={20} />, title: 'Flexible Timelines', desc: 'We work around your schedule and deadlines.' },
  { icon: <Mail size={20} />, title: 'Direct Communication', desc: 'Work directly with our team — no middlemen.' },
];

const Contact: React.FC = () => {
  const isMobile = useWindowWidth() < 900;

  return (
    <>
      <Helmet>
        <title>Contact | Elola Technologies – Let's Build Together</title>
        <meta name="description" content="Ready to start your project? Contact Elola Technologies to discuss your needs in AI, Web3, blockchain, app development, and more." />
        <link rel="canonical" href="https://elola.tech/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Elola Technologies" />
        <meta property="og:title" content="Contact | Elola Technologies – Let's Build Together" />
        <meta property="og:description" content="Ready to start your project? Contact Elola Technologies today." />
        <meta property="og:image" content="https://elola.tech/preview-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content="https://elola.tech/contact" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Contact | Elola Technologies – Let's Build Together" />
        <meta name="twitter:description" content="Ready to start your project? Contact Elola Technologies today." />
        <meta name="twitter:image" content="https://elola.tech/preview-image.png" />
      </Helmet>

      <div style={{ background: 'var(--bg-base)', minHeight: '100vh', paddingTop: 'var(--navbar-height)' }}>
        {/* Header */}
        <section style={{
          padding: 'clamp(50px, 7vw, 90px) var(--page-padding-x) clamp(40px, 5vw, 60px)',
          textAlign: 'center',
          background: 'linear-gradient(180deg, var(--bg-surface) 0%, var(--bg-base) 100%)',
          borderBottom: '1px solid var(--border-card)',
        }}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="section-tag" style={{ justifyContent: 'center' }}>
              <Mail size={13} />
              Get In Touch
            </div>
            <h1 style={{ fontSize: 'var(--text-h1)', fontWeight: 800, marginBottom: '16px' }}>
              Let's Build <span className="gradient-text">Together</span>
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'clamp(15px, 1.5vw, 17px)', maxWidth: '500px', margin: '0 auto' }}>
              Have a project in mind? We'd love to hear about it. Fill out the form and we'll be in touch shortly.
            </p>
          </motion.div>
        </section>

        {/* Main Content */}
        <section style={{
          padding: 'clamp(48px, 6vw, 80px) var(--page-padding-x)',
          maxWidth: 'var(--page-max-width)',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1.6fr',
          gap: 'clamp(32px, 5vw, 64px)',
          alignItems: 'start',
        }}>
          {/* Left: Perks */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
          >
            <h2 style={{ fontSize: 'var(--text-h3)', fontWeight: 700, marginBottom: '8px' }}>
              Why work with us?
            </h2>
            {perks.map(({ icon, title, desc }) => (
              <div key={title} style={{
                display: 'flex',
                gap: '16px',
                alignItems: 'flex-start',
                padding: '18px 20px',
                background: 'var(--bg-card)',
                border: '1px solid var(--border-card)',
                borderRadius: '12px',
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(109, 74, 255, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: 'var(--accent-purple-light)',
                }}>
                  {icon}
                </div>
                <div>
                  <h3 style={{ fontSize: '15px', fontWeight: 600, marginBottom: '4px' }}>{title}</h3>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>{desc}</p>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Right: Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <ContactForm />
          </motion.div>
        </section>
      </div>
    </>
  );
};

export default Contact;
